import type { IoTNode, ParcelValveBinding, PumpBindingView } from '../../lib/iotApi';
import {
  getAirHumidity,
  getAirTemperature,
  getBattery,
  getFlow,
  getNodeBattery,
  getNodeFlow,
  getNodeRelayChannels,
  getNodeValveState,
  getPressure,
  getSoilEc,
  getSoilMoisture,
  getSoilTemperature,
  isRelayCapable,
  isValveCapable,
} from '../../lib/liveSensorMetrics';

export interface EquipmentMetricRow {
  label: string;
  value: string;
}

export interface EquipmentCommissioningView {
  label: string;
  detail: string | null;
  source: 'valve-binding' | 'pump-binding' | 'none';
}

export const formatEquipmentValue = (
  value: number | undefined | null,
  digits = 1,
  unit = '',
): string =>
  value === undefined || value === null
    ? '—'
    : `${value.toFixed(digits)}${unit ? ` ${unit}` : ''}`;

export const equipmentType = (node: IoTNode): string => {
  const model = (node.model || '').toLowerCase();
  if (isRelayCapable(node.model)) return 'Relais / commande';
  if (isValveCapable(node.model)) return 'Contrôleur de vannes';
  if (model.includes('pressure')) return 'Capteur de pression';
  if (model.includes('soil') || model.includes('ecth')) return 'Capteur sol & air';
  if (model.includes('flow')) return 'Capteur de débit';
  return node.model || 'Équipement IoT';
};

export const equipmentMetrics = (node: IoTNode): EquipmentMetricRow[] => {
  const measures = node.latest?.measures;
  const rows: EquipmentMetricRow[] = [];
  const pressure = getPressure(measures);
  const flow = getNodeFlow(node) ?? getFlow(measures);
  const soilMoisture = getSoilMoisture(measures);
  const soilEc = getSoilEc(measures);
  const soilTemp = getSoilTemperature(measures);
  const airTemp = getAirTemperature(measures);
  const airHumidity = getAirHumidity(measures);
  const battery = getNodeBattery(node) ?? getBattery(measures) ?? node.battery_v ?? undefined;

  if (pressure !== undefined) rows.push({ label: 'Pression', value: formatEquipmentValue(pressure, 2, 'bar') });
  if (flow !== undefined) rows.push({ label: 'Débit', value: formatEquipmentValue(flow, 1, 'L/min') });
  if (soilMoisture !== undefined) rows.push({ label: 'Humidité sol', value: formatEquipmentValue(soilMoisture, 1, '%') });
  if (soilEc !== undefined) rows.push({ label: 'EC sol', value: formatEquipmentValue(soilEc, 0, 'µS/cm') });
  if (soilTemp !== undefined) rows.push({ label: 'Température sol', value: formatEquipmentValue(soilTemp, 1, '°C') });
  if (airTemp !== undefined) rows.push({ label: 'Température air', value: formatEquipmentValue(airTemp, 1, '°C') });
  if (airHumidity !== undefined) rows.push({ label: 'Humidité air', value: formatEquipmentValue(airHumidity, 1, '%') });
  if (battery !== undefined) rows.push({ label: 'Batterie', value: formatEquipmentValue(battery, 1, 'V') });

  const valveState = getNodeValveState(node);
  if (valveState !== undefined) rows.push({ label: 'Vanne', value: valveState === 1 ? 'Ouverte' : 'Fermée' });

  const relayChannels = getNodeRelayChannels(node);
  if (relayChannels) {
    for (const channel of relayChannels) {
      rows.push({
        label: channel.channel,
        value: channel.on === null ? 'Inconnu' : channel.on ? 'Activé' : 'Désactivé',
      });
    }
  }

  return rows;
};

const friendlyCommissioning = (status: string): string => {
  const normalized = status.trim().toUpperCase();
  if (!normalized) return 'Non renseigné';
  if (normalized === 'COMMISSIONED') return 'Commissionné';
  if (normalized === 'NOT_COMMISSIONED' || normalized === 'UNCOMMISSIONED') return 'Non commissionné';
  if (normalized === 'READY_FOR_FIELD') return 'Prêt terrain';
  if (normalized === 'PENDING' || normalized === 'TO_CONFIRM') return 'À confirmer';
  return status.replace(/_/g, ' ');
};

export const equipmentCommissioning = (
  node: IoTNode,
  parcelBindings: ParcelValveBinding[],
  pumps: PumpBindingView[],
): EquipmentCommissioningView => {
  const valveBindings = parcelBindings.filter((binding) => binding.controller_node_id === node.id);
  if (valveBindings.length > 0) {
    const statuses = Array.from(new Set(valveBindings.map((binding) => friendlyCommissioning(binding.commissioning_status))));
    return {
      label: statuses.length === 1 ? statuses[0] : statuses.join(' · '),
      detail: `${valveBindings.length} liaison${valveBindings.length > 1 ? 's' : ''} parcelle/vanne`,
      source: 'valve-binding',
    };
  }

  const pumpBindings = pumps.filter((binding) => binding.relay_node_id === node.id);
  if (pumpBindings.length > 0) {
    const statuses = Array.from(new Set(pumpBindings.map((binding) => friendlyCommissioning(binding.commissioning_status))));
    const channels = pumpBindings
      .map((binding) => binding.relay_channel)
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    return {
      label: statuses.length === 1 ? statuses[0] : statuses.join(' · '),
      detail: channels.length > 0 ? `Canal ${Array.from(new Set(channels)).join(', ')}` : 'Canal relais à confirmer',
      source: 'pump-binding',
    };
  }

  return {
    label: 'Non renseigné',
    detail: 'Aucune source de commissioning canonique pour cet équipement',
    source: 'none',
  };
};
